import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingBackButton from './FloatingBackButton';
import { PRIVACY_CONTENT } from '../data/privacyContent';

const Privacy: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const c = PRIVACY_CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <FloatingBackButton
        onBack={() => navigate(-1)}
        mode="inline"
        variant="with-text"
        text={c.backLabel}
        className="mb-6"
      />

      <h1 className="text-3xl font-bold mb-8 text-gray-900">
        {c.pageTitle}
      </h1>

      <div className="prose prose-lg max-w-none">
        <p className="text-gray-600 mb-6">
          <strong>{c.lastUpdatedLabel}</strong> {c.lastUpdatedDate}
        </p>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s1Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.s1P1}
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">
              <strong>{c.controllerLabel}</strong> {c.controllerValue}<br />
              <strong>{c.emailLabel}</strong> <a href="mailto:jogolinga@gmail.com" className="text-blue-600 hover:underline">jogolinga@gmail.com</a><br />
              <strong>{c.websiteLabel}</strong> <a href="https://jogolinga.app" className="text-blue-600 hover:underline">https://jogolinga.app</a>
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s2Title}
          </h2>

          <h3 className="text-xl font-semibold mb-3 text-gray-800">
            {c.s2_1Title}
          </h3>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.s2_1P1}
          </p>
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <ul className="list-disc pl-6 text-gray-700">
              {c.s2_1List.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <p className="font-semibold text-gray-800 mb-1">{c.limitedScopeLabel}</p>
            <p className="text-gray-700">
              {c.limitedScopeText}
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6">
            {c.s2_2Title}
          </h3>
          <p className="mb-3 text-gray-700">{c.s2_2IntroPre} <strong>{c.s2_2IntroBold}</strong> :</p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.s2_2List.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s3Title}
          </h2>

          <h3 className="text-xl font-semibold mb-3 text-gray-800">
            {c.s3_1Title}
          </h3>
          <div className="mb-4">
            <p className="font-semibold text-gray-800 mb-2">{c.authLabel}</p>
            <ul className="list-disc pl-6 text-gray-700">
              {c.authList.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="mb-4">
            <p className="font-semibold text-gray-800 mb-2">{c.progressLabel}</p>
            <p className="text-gray-700">
              {c.progressText}
            </p>
          </div>
          <div className="mb-4">
            <p className="font-semibold text-gray-800 mb-2">{c.communityLabel}</p>
            <p className="text-gray-700">
              {c.communityText}
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6">
            {c.s3_2Title}
          </h3>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.legalBasisList.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6">
            {c.s3_3Title}
          </h3>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.retentionList.map((item, i) => (
              <li key={i}><strong>{item.label}</strong> {item.text}</li>
            ))}
          </ul>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s4Title}
          </h2>

          <h3 className="text-xl font-semibold mb-3 text-gray-800">
            {c.s4_1Title}
          </h3>
          <div className="mb-3">
            <p className="font-semibold text-gray-800 mb-1">{c.subprocessorSupabaseName}</p>
            <p className="text-gray-700 mb-2">
              {c.subprocessorSupabaseText}
            </p>
          </div>
          <div className="mb-3">
            <p className="font-semibold text-gray-800 mb-1">{c.subprocessorGoogleName}</p>
            <p className="text-gray-700 mb-2">
              {c.subprocessorGoogleText1}{' '}
              <strong>{c.subprocessorGoogleAdSenseBold}</strong> {c.subprocessorGoogleText2}{' '}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {c.subprocessorGoogleLinkText}
              </a>.
            </p>
          </div>
          <div className="mb-3">
            <p className="font-semibold text-gray-800 mb-1">{c.subprocessorStripeName}</p>
            <p className="text-gray-700 mb-2">
              {c.subprocessorStripeText}
            </p>
          </div>
          <div className="mb-3">
            <p className="font-semibold text-gray-800 mb-1">{c.subprocessorVercelName}</p>
            <p className="text-gray-700 mb-2">
              {c.subprocessorVercelText}
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
            <p className="font-semibold text-gray-800 mb-1">{c.noCommercialLabel}</p>
            <p className="text-gray-700">
              {c.noCommercialText}
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6">
            {c.s4_2Title}
          </h3>
          <p className="mb-3 text-gray-700">{c.s4_2Intro}</p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.mandatorySharingList.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s5Title}
          </h2>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.s5List.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Section 6 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s6Title}
          </h2>
          <p className="mb-3 text-gray-700">
            {c.s6P1}
          </p>
          <p className="mb-3 text-gray-700">{c.s6P2}</p>
          <ol className="list-decimal pl-6 mb-4 text-gray-700">
            {c.s6Steps.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-gray-700">
              {c.s6NotePre} <strong>{c.s6NoteBold}</strong>.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s7Title}
          </h2>

          <h3 className="text-xl font-semibold mb-3 text-gray-800">
            {c.s7_1Title}
          </h3>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.s7_1List.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6">
            {c.s7_2Title}
          </h3>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.s7_2List.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>

          <h3 className="text-xl font-semibold mb-3 text-gray-800 mt-6">
            {c.s7_3Title}
          </h3>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li>{c.s7_3Item1}</li>
            <li>
              <strong>{c.adsenseCookiesBold}</strong> {c.adsenseCookiesText}{' '}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {c.adsenseCookiesLinkText}
              </a>.
            </li>
          </ul>
        </section>

        {/* Section 8 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s8Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.s8P1}
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.rightsList.map((item, i) => (
              <li key={i}><strong>{item.label}</strong> {item.text}</li>
            ))}
          </ul>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">
              <strong>{c.exerciseRightsBold}</strong> {c.exerciseRightsPre}{' '}
              <a href="mailto:jogolinga@gmail.com" className="text-blue-600 hover:underline">jogolinga@gmail.com</a>{' '}
              {c.exerciseRightsPost}
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s9Title}
          </h2>
          <p className="mb-4 text-gray-700 leading-relaxed">
            {c.s9Pre}{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              {c.s9LinkText}
            </a>{c.s9Post}
          </p>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <ul className="list-disc pl-6 text-gray-700">
              {c.s9List.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <p className="mb-3 text-gray-700 font-semibold">{c.applicablePoliciesLabel}</p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            {c.policyLinks.map((link, i) => (
              <li key={i}>
                <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {link.text}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 10 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s10Title}
          </h2>
          <p className="text-gray-700 leading-relaxed">
            {c.s10Text}
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            {c.s11Title}
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="text-gray-700">
              <strong>{c.contactEmailLabel}</strong>{' '}
              <a href="mailto:jogolinga@gmail.com" className="text-blue-600 hover:underline">jogolinga@gmail.com</a><br />
              <strong>{c.appLabel}</strong> {c.appValue}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="font-semibold text-gray-800 mb-2">{c.gdprComplaintTitle}</p>
            <p className="text-gray-700">
              <strong>{c.competentAuthorityLabel}</strong> {c.competentAuthorityValue}<br />
              <strong>{c.siteLabel}</strong>{' '}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://www.cnil.fr
              </a>
            </p>
          </div>
          <p className="text-gray-600 text-sm mt-4">
            {c.footerTextPre}
            <strong> {c.footerDateLabel}</strong> {c.footerDate}
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
